'use client'

import { useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Megaphone } from 'lucide-react'
import { DemoFrame } from './demoKit'
import { useOnScreen, useSequence } from './demoHooks'
import { ResultStamp } from './demoParts'

// GHL Ads Management: one Facebook lead, two journeys. Without a CRM link it
// waits in a spreadsheet and goes cold; with GHL Prime's build it lands in
// the pipeline in seconds and the closed deal is credited to the ad. Plays
// the leaky version first, then the fixed one, until the visitor picks.

const AD = { name: 'Spring tune-up offer', audience: 'Homeowners · 15 mi radius' }

const PATHS = {
  without: {
    steps: [
      { time: '0:00', text: 'Lead fills in the Facebook form' },
      { time: '0:01', text: 'Lead lands in a spreadsheet export' },
      { time: '2h 40m', text: 'Notification email still unread' },
      { time: '3h 12m', text: 'Someone finally calls back' },
      { time: '3h 12m', text: 'Already booked with a competitor' },
      { time: '—', text: 'No way to tell which ad worked' },
    ],
    result: { tone: 'bad', text: 'Lead went cold' },
  },
  with: {
    steps: [
      { time: '0:00', text: 'Lead fills in the Facebook form' },
      { time: '0:02', text: 'Contact created in GoHighLevel' },
      { time: '0:03', text: 'Placed in the pipeline: New lead' },
      { time: '0:05', text: 'Speed-to-lead text sent' },
      { time: '0:06', text: 'Assigned rep notified' },
      { time: 'Day 4', text: 'Deal won: $2,400, credited to this ad' },
    ],
    result: { tone: 'good', text: 'Closed · ad gets the credit' },
  },
}
const DELAYS = [500, 1000, 1000, 1000, 1000, 1300, 700]

export default function AdsLoop() {
  const [ref, onScreen] = useOnScreen()
  const [path, setPath] = useState('without')
  const [run, setRun] = useState(0)
  const [auto, setAuto] = useState(true)
  const data = PATHS[path]
  const step = useSequence(DELAYS, { run: onScreen, resetKey: `${path}-${run}` })
  const finished = step >= DELAYS.length

  useEffect(() => {
    if (!auto || path !== 'without' || !finished) return undefined
    const timer = setTimeout(() => setPath('with'), 1800)
    return () => clearTimeout(timer)
  }, [auto, path, finished])

  const pick = (key) => { setAuto(false); setPath(key); setRun((n) => n + 1) }

  return (
    <DemoFrame
      frameRef={ref}
      className={`svd-ads is-${path}`}
      title="One ad lead, two journeys"
      label="Demo: a Facebook ad lead with and without a GoHighLevel connection"
      tabs={[{ key: 'without', label: 'Without GoHighLevel', tone: 'bad' }, { key: 'with', label: 'With GHL Prime', tone: 'good' }]}
      activeTab={path}
      onTab={pick}
      onReplay={() => pick(path)}
    >
      <div className="svd-ad-card">
        <span className="svd-ad-icon" aria-hidden="true"><Megaphone size={16} /></span>
        <span><strong>{AD.name}</strong><small>Facebook lead ad · {AD.audience}</small></span>
        <span className="svd-chip">Lead form</span>
      </div>
      <ol className="svd-journey">
        <AnimatePresence initial={false}>
          {data.steps.slice(0, Math.min(step, data.steps.length)).map((s, i) => (
            <motion.li
              key={`${path}-${run}-${i}`}
              className={path === 'with' ? 'is-good' : i >= 2 ? 'is-bad' : ''}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              <time>{s.time}</time>
              <span>{s.text}</span>
            </motion.li>
          ))}
        </AnimatePresence>
      </ol>
      <ResultStamp show={finished} tone={data.result.tone} text={data.result.text} id={`${path}-${run}`} />
    </DemoFrame>
  )
}
